import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import Image from '@tiptap/extension-image';
import { ImageOptions } from '@tiptap/extension-image';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { firstValueFrom } from 'rxjs';

/**
 * Custom Image extension that loads images with authentication.
 * This extension replaces regular image URLs with blob URLs fetched via authenticated requests.
 *
 * Images are loaded asynchronously and cached to avoid redundant requests.
 */
export interface AuthImageOptions extends ImageOptions {
  httpClient: HttpClient;
  auth: Auth;
}

// Cache for blob URLs to avoid re-fetching
const blobUrlCache = new Map<string, string>();

// Track loading state
const loadingImages = new Set<string>();

/**
 * Helper function to determine if a URL needs authentication
 * @param src Image source URL
 * @returns true if the URL requires authentication
 */
function needsAuth(src: string): boolean {
  if (!src) return false;

  // Only apply auth to our API endpoints
  // Check for relative URLs starting with /api/ or /knowledge-base/
  // Also check for absolute URLs containing these paths
  return (
    src.startsWith('/api/') ||
    src.startsWith('/knowledge-base/') ||
    src.includes('/api/knowledge-base/') ||
    (src.includes('://') && (src.includes('/api/') || src.includes('/knowledge-base/')))
  );
}

/**
 * Load an image with authentication and convert to blob URL
 */
async function loadAuthenticatedImage(src: string, httpClient: HttpClient, auth: Auth): Promise<string> {
  // Check cache first
  if (blobUrlCache.has(src)) {
    return blobUrlCache.get(src)!;
  }

  // Avoid duplicate requests
  if (loadingImages.has(src)) {
    // Wait for the existing request to complete
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (blobUrlCache.has(src)) {
          clearInterval(checkInterval);
          resolve(blobUrlCache.get(src)!);
        }
      }, 100);
    });
  }

  loadingImages.add(src);

  try {
    // Get auth token
    const user = await auth.currentUser;
    const token = user ? await user.getIdToken() : null;

    // Fetch with authentication using HttpClient directly
    const blob = await firstValueFrom(
      httpClient.get(src, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob',
      }),
    );

    if (!blob) {
      throw new Error('No blob returned from request');
    }

    const blobUrl = URL.createObjectURL(blob);

    // Cache the blob URL
    blobUrlCache.set(src, blobUrl);

    return blobUrl;
  } catch (error) {
    // Don't cache errors - we want to retry on next render
    // Don't return the original src - that would cause an unauthenticated request
    // Instead, return an error placeholder (but don't cache it)
    const errorPlaceholder =
      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="100"%3E%3Crect width="200" height="100" fill="%23fee"/%3E%3Ctext x="100" y="50" text-anchor="middle" dy=".3em" fill="%23c00" font-size="12"%3EFailed to load%3C/text%3E%3C/svg%3E';
    return errorPlaceholder;
  } finally {
    loadingImages.delete(src);
  }
}

/**
 * ProseMirror plugin that handles authenticated image loading
 */
function createAuthImagePlugin(httpClient: HttpClient, auth: Auth) {
  const loadImagesInView = (view: any) => {
    // Find all images in the document
    view.state.doc.descendants((node: any, pos: number) => {
      if (node.type.name === 'image') {
        const src = node.attrs.src;

        if (src && needsAuth(src) && !blobUrlCache.has(src) && !loadingImages.has(src)) {
          // Load the image asynchronously
          loadAuthenticatedImage(src, httpClient, auth)
            .then((blobUrl) => {
              // Find the DOM node and update it
              const domAtPos = view.domAtPos(pos + 1);
              const imgElement = domAtPos.node as HTMLElement;

              // Find the actual img tag (might be the node itself or a child)
              const img =
                imgElement.tagName === 'IMG' ? (imgElement as HTMLImageElement) : imgElement.querySelector('img');

              if (img) {
                const originalSrc = img.getAttribute('data-original-src');

                // Check if this is the image we're looking for
                if (originalSrc === src || img.getAttribute('src') === src || img.src.endsWith(src)) {
                  img.src = blobUrl;
                  img.removeAttribute('data-original-src');
                }
              }
            })
            .catch((error) => {
              console.error('[AuthImage] Failed to load image:', src, error);
            });
        }
      }
    });
  };

  return new Plugin({
    key: new PluginKey('authImage'),
    view(editorView) {
      // Load images immediately on mount
      loadImagesInView(editorView);

      return {
        update(view, prevState) {
          // Only scan for new images if the document actually changed
          if (!prevState || view.state.doc !== prevState.doc) {
            loadImagesInView(view);
          }
        },
      };
    },
  });
}

export const AuthImage = Image.extend<AuthImageOptions>({
  name: 'image',

  addOptions() {
    return {
      ...this.parent?.(),
      inline: true,
      allowBase64: true,
      HTMLAttributes: {
        class: 'inline-image',
      },
      httpClient: null as any,
      auth: null as any,
    };
  },

  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() || [];

    if (!this.options.httpClient || !this.options.auth) {
      return parentPlugins;
    }

    return [...parentPlugins, createAuthImagePlugin(this.options.httpClient, this.options.auth)];
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (dom) => {
          const element = dom as HTMLElement;
          const src = element.getAttribute('src');
          const alt = element.getAttribute('alt');
          const title = element.getAttribute('title');

          return {
            src,
            alt,
            title,
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const src = node.attrs.src;

    // Check if we have a cached blob URL, use it if available
    const cachedBlobUrl = blobUrlCache.get(src);

    // For auth-required images without a cached blob URL, use a transparent placeholder
    // to prevent the browser from making an unauthenticated request
    if (needsAuth(src) && !cachedBlobUrl) {
      return [
        'img',
        {
          ...HTMLAttributes,
          src: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3C/svg%3E',
          'data-original-src': src,
          loading: 'lazy',
        },
      ];
    }

    const finalSrc = cachedBlobUrl || src;
    return ['img', { ...HTMLAttributes, src: finalSrc }];
  },
});

/**
 * Clean up blob URLs when they're no longer needed
 * Call this when unmounting components or cleaning up
 */
export function revokeAllAuthImageBlobUrls() {
  for (const blobUrl of blobUrlCache.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  blobUrlCache.clear();
  loadingImages.clear();
}
